/**
 * Workspace shell — sidebar (workspaces / projects) + main view.
 *
 * Sidebar uses Link from TanStack Router so navigation is client-side.
 * The shell mounts the ⌘K command palette and the top bar.
 */

import { LayoutGrid, CheckSquare, Search, Menu } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Outlet, useNavigate, useParams, useLocation, Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { api, ApiError } from '@/lib/api'
import { hasSession } from '@/lib/auth'
import { useAuth } from '@/features/auth/authContext'
import { CommandPaletteMount, openCommandPalette } from '@/components/shell/CommandPalette'
import { NotificationsBell } from '@/features/notifications/Notifications'
import { UserMenu } from '@/components/user/UserMenu'
import { useCreateProject } from '@/features/projects/board'
import { useCreateWorkspace, useWorkspaces, useArchiveWorkspace } from '@/features/workspaces/workspaces'
import { usePageTree } from '@/features/notebook/pages'
import { FridayMark } from '@/components/branding/FridayMark'
import { FridayLockup } from '@/components/branding/FridayLockup'
import { FridayCorner } from '@/components/branding/FridayCorner'
import { PageTree } from '@/components/notebook/PageTree'
import { CreateWorkspaceDialog } from '@/components/workspace/CreateWorkspaceDialog'
import { CreateProjectDialog } from '@/components/workspace/CreateProjectDialog'
import { ConfirmDialog, KebabMenu, type KebabMenuItem } from '@/components/feedback'
import { toast } from '@/features/feedback/toast'
import { cn } from '@/lib/utils'
import { useTrackSettingsOrigin } from '@/features/settings/settingsOrigin'
import type { ReactNode } from 'react'
import type { Workspace, Project } from '@friday/shared/realtime'

const SIDEBAR_COLLAPSED_KEY = 'friday:sidebar-collapsed'
// FRIDAY-109: the sidebar is resizable; the width is remembered per browser.
const SIDEBAR_WIDTH_KEY = 'friday:sidebar-width'
const SIDEBAR_DEFAULT_WIDTH = 240
const SIDEBAR_MIN_WIDTH = 200
const SIDEBAR_MAX_WIDTH = 480
const SIDEBAR_KEYBOARD_STEP = 16

export function WorkspaceShell({ children }: { children?: ReactNode }) {
  const { user, loading, logout } = useAuth()
  const navigate = useNavigate()
  const params = useParams({ strict: false }) as { wsId?: string; pKey?: string; pageId?: string }
  const location = useLocation()

  useEffect(() => {
    if (loading) return
    // Read the storage truth, not just the React user state. `setUser` in the
    // auth context can lag a router-driven render (TanStack Router uses
    // useSyncExternalStore, which can commit ahead of the pending setUser),
    // so a fresh login would otherwise briefly bounce us back to /login.
    if (!user && !hasSession()) navigate({ to: '/login', search: { next: undefined } })
  }, [user, loading, navigate])

  // Remember where the user was before they opened a settings screen, so
  // "Close settings" can take them back to it.
  useTrackSettingsOrigin()

  // Sidebar UI state: mobile drawer (`open`) and desktop collapse (`collapsed`).
  const [open, setOpen] = useState(false)
  useEffect(() => {
    if (!open) return
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', close)
    return () => document.removeEventListener('keydown', close)
  }, [open])
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1'
  })
  useEffect(() => {
    if (typeof window === 'undefined') return
    window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? '1' : '0')
  }, [collapsed])

  // FRIDAY-109: the sidebar width. It lives in a CSS custom property so the
  // mobile drawer keeps its own width and only the desktop panel resizes.
  const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
    if (typeof window === 'undefined') return SIDEBAR_DEFAULT_WIDTH
    const raw = Number(window.localStorage.getItem(SIDEBAR_WIDTH_KEY))
    return Number.isFinite(raw) && raw >= SIDEBAR_MIN_WIDTH && raw <= SIDEBAR_MAX_WIDTH
      ? raw
      : SIDEBAR_DEFAULT_WIDTH
  })
  useEffect(() => {
    if (typeof window === 'undefined') return
    window.localStorage.setItem(SIDEBAR_WIDTH_KEY, String(sidebarWidth))
  }, [sidebarWidth])

  const asideRef = useRef<HTMLElement | null>(null)
  const clampWidth = useCallback(
    (value: number) => Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, Math.round(value))),
    [],
  )
  const resizeSidebar = useCallback(
    (delta: number) => setSidebarWidth((w) => clampWidth(w + delta)),
    [clampWidth],
  )

  /** Drag the right edge of the sidebar to make it wider or narrower. */
  const startResize = (event: React.PointerEvent<HTMLDivElement>) => {
    const aside = asideRef.current
    if (!aside) return
    event.preventDefault()
    // Dragging the rail expands the sidebar to the dragged width.
    if (collapsed) setCollapsed(false)
    const startX = event.clientX
    const startWidth = aside.getBoundingClientRect().width
    const previousCursor = document.body.style.cursor
    const previousSelect = document.body.style.userSelect
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    const onMove = (moveEvent: PointerEvent) => {
      setSidebarWidth(clampWidth(startWidth + (moveEvent.clientX - startX)))
    }
    const onUp = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      document.body.style.cursor = previousCursor
      document.body.style.userSelect = previousSelect
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }

  // Close the mobile drawer whenever the route changes so tapping a link
  // doesn't leave a full-screen sidebar covering the new page.
  useEffect(() => {
    setOpen(false)
  }, [location.pathname])

  const { data: workspaces } = useWorkspaces()
  const createWorkspace = useCreateWorkspace()
  const archiveWorkspace = useArchiveWorkspace()

  const activeWs = useMemo(() => {
    if (params.wsId) return params.wsId
    return workspaces?.[0]?.id
  }, [workspaces, params.wsId])

  // Only redirect to /w/$wsId when we're sitting on the bare `/w` index —
  // NOT on `/me`, `/me/archive`, `/settings`, or any other top-level route
  // that just happens to render this shell. Previously this fired for every
  // route under the shell, sending /me and /me/archive straight into the
  // workspace overview.
  useEffect(() => {
    if (!activeWs) return
    if (params.wsId) return
    if (location.pathname !== '/w' && location.pathname !== '/w/') return
    navigate({ to: '/w/$wsId', params: { wsId: activeWs } })
  }, [activeWs, params.wsId, location.pathname, navigate])

  const { data: projects } = useQuery({
    queryKey: ['workspaces', activeWs, 'projects'],
    queryFn: () => api<Project[]>(`/api/v1/workspaces/${activeWs}/projects`),
    enabled: !!activeWs,
  })

  const { data: rootPages } = usePageTree(activeWs)
  const rootPageList = (rootPages ?? [])
    .filter((p) => !p.parentPageId && !p.archivedAt)
    .sort((a, b) => a.position - b.position)

  const [creatingProject, setCreatingProject] = useState(false)
  const [creatingWorkspace, setCreatingWorkspace] = useState(false)
  const [pendingArchive, setPendingArchive] = useState<Workspace | null>(null)
  const createProject = useCreateProject(activeWs)

  if (!user) return null

  return (
    <div className="workspace-shell bg-app text-app flex h-full">
      <aside
        ref={asideRef}
        data-sidebar
        data-open={open ? 'true' : 'false'}
        style={{ '--friday-sidebar-width': `${sidebarWidth}px` } as React.CSSProperties}
        className={cn(
          'border-app-border bg-app-subtle flex w-60 shrink-0 flex-col border-r',
          // Mobile: drawer-style overlay. z-40 on mobile so it sits above
          // the page tree drawer when both happen to be open.
          // On desktop: z-50 so children (notably the bell dropdown) stack
          // above sibling overlays like the notebook page tree (z-40).
          'fixed inset-y-0 left-0 z-40 transition-transform duration-200 md:relative md:inset-auto md:z-50 md:translate-x-0',
          // On mobile, hide by sliding off-screen unless `open`
          open ? 'translate-x-0' : '-translate-x-full md:translate-x-0',
          // Collapse: shrink-to-icon mode (kept narrow). The expand handle
          // sits at the right edge of the <aside>, so it floats just
          // outside the narrow column — visible no matter the width.
          collapsed && 'md:w-14',
          // FRIDAY-109: the desktop panel follows the remembered width. The
          // `!` keeps it above `w-60`; the mobile drawer keeps 240px.
          !collapsed && 'md:!w-[var(--friday-sidebar-width)]',
        )}
      >
        <div
          className={cn(
            'border-surface-200 dark:border-surface-800 flex items-center gap-2 border-b py-3',
            collapsed ? 'md:justify-center md:px-1' : 'px-3',
          )}
        >
          <FridayLockup className={cn(collapsed && 'md:hidden')} />
          {/* Collapsed (desktop): the full wordmark scaled to the 56px rail.
              The compact "Fr" mark clipped to just an "F" (FRIDAY-6). */}
          {collapsed && <FridayMark size={13} className="hidden md:block" title="Friday" />}
          <button
            type="button"
            onClick={() => setOpen(false)}
            className={cn(
              'text-surface-500 hover:bg-surface-100 dark:hover:bg-surface-800 rounded p-1',
              // Mobile only: the drawer has its own close. On desktop the
              // mobile close button would be invisible-but-clickable, so
              // hide it on md+.
              'md:hidden',
            )}
            aria-label="Close sidebar"
            title="Close sidebar"
          >
            ×
          </button>
          {!collapsed && <div className="flex-1" />}
          {/* The expand/collapse handle — always visible. When the sidebar
              is collapsed, it centers over the mark so the user can always
              grab it without knowing the wordmark is hidden. */}
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            className={cn(
              'text-surface-500 hover:bg-surface-100 dark:hover:bg-surface-800 rounded p-1',
              // Show on desktop only. On mobile the workspace shell already
              // has its own hamburger in the main area, and the drawer is
              // opened/closed with the scrim.
              'hidden md:inline-flex',
              collapsed &&
                'border-surface-200 text-surface-600 dark:border-surface-700 dark:bg-surface-800 dark:text-surface-200 absolute -right-3 top-3 z-10 h-6 w-6 items-center justify-center rounded-full border bg-white shadow-sm',
            )}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-pressed={collapsed}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? '»' : '«'}
          </button>
        </div>

        <div className="border-app-border shrink-0 border-b px-3 py-3">
          <div className={cn('flex items-center gap-1.5', collapsed && 'md:justify-center')}>
            <button
              data-testid="command-palette-trigger"
              onClick={openCommandPalette}
              title="Command palette (⌘K)"
              className={cn(
                'border-surface-300 dark:border-surface-700 dark:bg-surface-800 text-surface-500 hover:border-brand-500 flex flex-1 items-center gap-2 rounded-md border bg-white px-2 py-1.5 text-left text-xs',
                collapsed && 'md:flex-none md:justify-center md:px-1.5',
              )}
            >
              {collapsed ? (
                <span>⌘K</span>
              ) : (
                <>
                  <span>Jump to…</span>
                  <span className="kbd ml-auto">⌘K</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Scrollable middle — the user menu stays pinned to the
            bottom of the sidebar however long the lists get (FRIDAY-7). */}
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="px-2 py-2">
            <div
              className={cn(
                'text-surface-500 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider',
                collapsed && 'md:hidden',
              )}
            >
              Personal
            </div>
            <Link
              to="/me"
              title="My Work"
              className={cn(
                'text-surface-700 dark:text-surface-200 hover:bg-surface-100 dark:hover:bg-surface-800 flex items-center gap-2 rounded-md px-2 py-1.5 text-sm',
                collapsed && 'md:justify-center md:gap-0 md:px-0',
              )}
            >
              <span className="bg-surface-300 dark:bg-surface-700 flex h-5 w-5 items-center justify-center rounded text-[10px]">
                ✓
              </span>
              <span className={cn(collapsed && 'md:hidden')}>My Work</span>
            </Link>
            <Link
              to="/me/archive"
              title="Archive"
              className={cn(
                'text-surface-700 dark:text-surface-200 hover:bg-surface-100 dark:hover:bg-surface-800 mt-0.5 flex items-center gap-2 rounded-md px-2 py-1.5 text-sm',
                collapsed && 'md:justify-center md:gap-0 md:px-0',
              )}
            >
              <span className="bg-surface-300 dark:bg-surface-700 flex h-5 w-5 items-center justify-center rounded text-[10px]">
                ⌫
              </span>
              <span className={cn(collapsed && 'md:hidden')}>Archive</span>
            </Link>
          </div>

          <div className="px-2 py-2">
            <div
              className={cn(
                'text-surface-500 flex items-center justify-between px-2 py-1 text-[10px] font-semibold uppercase tracking-wider',
                collapsed && 'md:hidden',
              )}
            >
              <span>Workspaces</span>
              <button
                onClick={() => setCreatingWorkspace(true)}
                className="hover:bg-surface-100 dark:hover:bg-surface-800 rounded p-0.5"
                title="New workspace"
              >
                +
              </button>
            </div>
            {(workspaces ?? []).map((w) => {
              const isOwner = w.role === 'owner'
              const kebabItems: KebabMenuItem[] = isOwner
                ? [
                    {
                      key: 'archive',
                      label: 'Archive workspace…',
                      destructive: true,
                      onSelect: () => setPendingArchive(w),
                    },
                  ]
                : []
              return (
                <div
                  key={w.id}
                  className={cn(
                    'group relative flex items-center gap-1 rounded-md',
                    activeWs === w.id
                      ? 'bg-brand-500/10 text-brand-600 dark:text-brand-300'
                      : 'text-surface-700 dark:text-surface-200 hover:bg-surface-100 dark:hover:bg-surface-800',
                  )}
                >
                  <Link
                    to="/w/$wsId"
                    params={{ wsId: w.id }}
                    title={w.name}
                    className={cn(
                      'flex flex-1 items-center gap-2 rounded-md px-2 py-1.5 text-sm',
                      collapsed && 'md:justify-center md:gap-0 md:px-0',
                    )}
                  >
                    <span className="bg-surface-300 dark:bg-surface-700 flex h-5 w-5 items-center justify-center rounded font-mono text-[10px]">
                      {(w.slug || w.name || 'WS').slice(0, 2).toUpperCase()}
                    </span>
                    <span className={cn('truncate', collapsed && 'md:hidden')}>{w.name}</span>
                  </Link>
                  {!collapsed && kebabItems.length > 0 && (
                    <div className="pr-1 opacity-0 focus-within:opacity-100 group-hover:opacity-100">
                      <KebabMenu items={kebabItems} ariaLabel={`Actions for ${w.name}`} />
                    </div>
                  )}
                </div>
              )
            })}
            {(workspaces?.length ?? 0) === 0 && !creatingWorkspace && !collapsed && (
              <button
                onClick={() => setCreatingWorkspace(true)}
                className="text-surface-500 hover:text-surface-700 dark:hover:text-surface-300 block w-full px-2 py-2 text-left text-xs"
              >
                Create your first workspace →
              </button>
            )}
          </div>

          {/* Pages (FRIDAY-69): the full page tree, with sub-pages folded
              into their parents. The collapsed desktop rail shows the
              top-level pages as icons only. */}
          {activeWs && (
            <div className={cn(collapsed && 'md:hidden')}>
              <PageTree wsId={activeWs} notebookLink />
            </div>
          )}
          {collapsed && (
            <div className="hidden px-2 py-2 md:block">
              {rootPageList.map((p) => {
                const active = p.id === params.pageId
                return (
                  <Link
                    key={p.id}
                    to="/w/$wsId/n/$pageId"
                    params={{ wsId: activeWs ?? '', pageId: p.id }}
                    title={p.title}
                    aria-current={active ? 'page' : undefined}
                    className={cn(
                      'flex items-center justify-center rounded-md py-1.5 text-sm',
                      active
                        ? 'bg-brand-500/10 text-brand-600 dark:text-brand-300'
                        : 'hover:bg-surface-100 dark:hover:bg-surface-800',
                    )}
                  >
                    {p.icon ?? '📄'}
                  </Link>
                )
              })}
            </div>
          )}

          <div className="px-2 py-2">
            <div
              className={cn(
                'text-surface-500 flex items-center justify-between px-2 py-1 text-[10px] font-semibold uppercase tracking-wider',
                collapsed && 'md:hidden',
              )}
            >
              <span>Projects</span>
              <button
                onClick={() => setCreatingProject(true)}
                className="hover:bg-surface-100 dark:hover:bg-surface-800 rounded p-0.5"
                title="New project"
                disabled={!activeWs}
              >
                +
              </button>
            </div>
            {(projects ?? []).map((p) => {
              // FRIDAY-68: the board the user is on stays highlighted,
              // including its task, settings and member sub-routes.
              const active = params.wsId === activeWs && params.pKey === p.key
              return (
                <Link
                  key={p.id}
                  to="/w/$wsId/p/$pKey"
                  params={{ wsId: activeWs ?? '', pKey: p.key }}
                  title={p.name}
                  aria-current={active ? 'page' : undefined}
                  className={cn(
                    'group flex items-center gap-2 rounded-md px-2 py-1.5 text-sm',
                    active
                      ? 'bg-brand-500/10 text-brand-600 dark:text-brand-300 font-medium'
                      : 'text-surface-700 dark:text-surface-200 hover:bg-surface-100 dark:hover:bg-surface-800',
                    collapsed && 'md:justify-center md:gap-0 md:px-0',
                  )}
                >
                  <span
                    className="flex h-5 w-5 items-center justify-center rounded font-mono text-[10px] text-white"
                    style={{ backgroundColor: p.color }}
                  >
                    {(p.key || 'P').slice(0, 2)}
                  </span>
                  {p.icon && (
                    <span className={cn('shrink-0 text-sm', collapsed && 'md:hidden')} aria-hidden="true">
                      {p.icon}
                    </span>
                  )}
                  <span className={cn('truncate', collapsed && 'md:hidden')}>{p.name}</span>
                </Link>
              )
            })}
            {(projects?.length ?? 0) === 0 && !collapsed && (
              <div className="text-surface-500 px-2 py-2 text-xs">No projects yet.</div>
            )}
          </div>
        </div>

        {/* One framed panel from the movie — OG Friday only, and only while
            the side navigation is expanded. */}
        {!collapsed && (
          <div className="px-3 pt-2">
            <FridayCorner />
          </div>
        )}

        <div className="border-surface-200 dark:border-surface-800 text-surface-500 shrink-0 border-t px-3 py-3 text-xs">
          <div className="mt-2">
            <UserMenu />
          </div>
        </div>

        {/*
          FRIDAY-109: drag the right edge to resize the sidebar. It is a
          separator, so arrow keys resize it too, and a double click goes
          back to the default width.
        */}
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize sidebar"
          aria-valuenow={collapsed ? 56 : sidebarWidth}
          aria-valuemin={collapsed ? 56 : SIDEBAR_MIN_WIDTH}
          aria-valuemax={collapsed ? 56 : SIDEBAR_MAX_WIDTH}
          tabIndex={0}
          onPointerDown={startResize}
          onDoubleClick={() => setSidebarWidth(SIDEBAR_DEFAULT_WIDTH)}
          onKeyDown={(e) => {
            // The collapsed rail has no width to resize: the first arrow key
            // opens the panel again.
            if (collapsed && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
              e.preventDefault()
              setCollapsed(false)
              return
            }
            if (e.key === 'ArrowLeft') {
              e.preventDefault()
              resizeSidebar(-SIDEBAR_KEYBOARD_STEP)
            } else if (e.key === 'ArrowRight') {
              e.preventDefault()
              resizeSidebar(SIDEBAR_KEYBOARD_STEP)
            } else if (e.key === 'Home') {
              e.preventDefault()
              setSidebarWidth(SIDEBAR_MIN_WIDTH)
            } else if (e.key === 'End') {
              e.preventDefault()
              setSidebarWidth(SIDEBAR_MAX_WIDTH)
            }
          }}
          className="focus-visible:bg-brand-500/40 hover:bg-brand-500/30 group absolute right-0 top-0 z-20 hidden h-full w-1 cursor-col-resize outline-none md:block"
        >
          <span className="bg-surface-300 dark:bg-surface-700 absolute left-1/2 top-1/2 h-8 w-0.5 -translate-x-1/2 -translate-y-1/2 rounded-full opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100" />
        </div>
      </aside>

      {/* Mobile scrim — click anywhere outside the drawer to close it. */}
      {open && (
        <button
          type="button"
          aria-label="Close sidebar"
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-30 bg-black/40 md:hidden"
        />
      )}

      <main className="workspace-main bg-app text-app relative min-w-0 flex-1 overflow-auto">
        {/* Mobile-only top bar with the menu toggle. Desktop hides it. */}
        <div className="border-app-border bg-app-subtle/90 sticky top-0 z-20 flex items-center gap-2 border-b px-3 py-2 backdrop-blur md:hidden">
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="text-surface-700 hover:bg-surface-100 dark:text-surface-200 dark:hover:bg-surface-800 rounded-md p-1.5"
            aria-label="Open sidebar"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <line x1="4" y1="6" x2="20" y2="6" />
              <line x1="4" y1="12" x2="20" y2="12" />
              <line x1="4" y1="18" x2="20" y2="18" />
            </svg>
          </button>
          <FridayLockup size="sm" />
          <div className="ml-auto">
            <NotificationsBell />
          </div>
        </div>
        <div className="workspace-content">{children ?? <Outlet />}</div>
        {!(children ?? null) && !creatingWorkspace && (workspaces?.length ?? 0) === 0 && (
          <div className="flex h-full items-center justify-center p-8">
            <div className="card max-w-md p-6 text-center">
              <h2 className="mb-2 text-lg font-semibold">No workspace yet</h2>
              <p className="text-surface-500 mb-4 text-sm">
                Create a workspace to start adding projects and tasks. Workspaces are containers that you can
                share with teammates.
              </p>
              <button onClick={() => setCreatingWorkspace(true)} className="btn-primary">
                Create your first workspace
              </button>
            </div>
          </div>
        )}
      </main>
      <nav
        aria-label="Mobile navigation"
        className="mobile-nav border-app-border bg-app fixed inset-x-0 bottom-0 z-20 grid grid-cols-4 border-t md:hidden"
      >
        <Link
          to="/w/$wsId"
          params={{ wsId: activeWs ?? '' }}
          aria-current={location.pathname.startsWith('/w/') ? 'page' : undefined}
        >
          <LayoutGrid size={20} />
          <span>Workspaces</span>
        </Link>
        <Link to="/me" aria-current={location.pathname === '/me' ? 'page' : undefined}>
          <CheckSquare size={20} />
          <span>My Work</span>
        </Link>
        <button onClick={openCommandPalette}>
          <Search size={20} />
          <span>Search</span>
        </button>
        <button onClick={() => setOpen((value) => !value)} aria-expanded={open}>
          <Menu size={20} />
          <span>Menu</span>
        </button>
      </nav>
      {/* Desktop: notifications live at the far right of the top bar
          (FRIDAY-8). Page headers reserve room for it with `md:pr-14`;
          mobile renders the bell in the shell's own top bar above. */}
      <div className="fixed right-3 top-1 z-30 hidden md:block">
        <NotificationsBell />
      </div>
      <CommandPaletteMount />
      {creatingProject && (
        <CreateProjectDialog
          onClose={() => setCreatingProject(false)}
          onSubmit={async (input) => {
            try {
              const project = await createProject.mutateAsync(input)
              setCreatingProject(false)
              toast(`Project "${project.name}" created`, { kind: 'success' })
              if (activeWs) {
                navigate({ to: '/w/$wsId/p/$pKey', params: { wsId: activeWs, pKey: project.key } })
              }
            } catch (e) {
              const msg = e instanceof ApiError ? e.message : 'Failed to create project'
              toast(msg, { kind: 'error' })
            }
          }}
          busy={createProject.isPending}
        />
      )}
      {creatingWorkspace && (
        <CreateWorkspaceDialog
          onClose={() => setCreatingWorkspace(false)}
          busy={createWorkspace.isPending}
          onSubmit={async (input) => {
            try {
              const ws = await createWorkspace.mutateAsync(input)
              setCreatingWorkspace(false)
              toast(`Workspace "${ws.name}" created`, { kind: 'success' })
              navigate({ to: '/w/$wsId', params: { wsId: ws.id } })
            } catch (e) {
              const msg = e instanceof ApiError ? e.message : 'Failed to create workspace'
              toast(msg, { kind: 'error' })
            }
          }}
        />
      )}
      <ConfirmDialog
        open={!!pendingArchive}
        title={`Archive "${pendingArchive?.name ?? ''}"?`}
        body={
          <div className="text-surface-600 dark:text-surface-300 space-y-2 text-sm">
            <p>This hides the workspace from the sidebar. All of its projects and tasks are archived too.</p>
            <p className="text-surface-500 text-xs">
              Nothing is permanently deleted — it can be restored by an admin later.
            </p>
          </div>
        }
        confirmLabel="Archive workspace"
        destructive
        busy={archiveWorkspace.isPending}
        onClose={() => !archiveWorkspace.isPending && setPendingArchive(null)}
        onConfirm={async () => {
          if (!pendingArchive) return
          const archived = pendingArchive
          try {
            const result = await archiveWorkspace.mutateAsync(archived.id)
            setPendingArchive(null)
            toast(
              `Workspace "${archived.name}" archived (${result.cascadedProjects} projects, ${result.cascadedTasks} tasks)`,
              { kind: 'success' },
            )
            // Navigate to the next available workspace, or fall back to /me.
            const remaining = (workspaces ?? []).filter((w) => w.id !== archived.id)
            const next = remaining[0]?.id
            if (next) {
              navigate({ to: '/w/$wsId', params: { wsId: next } })
            } else {
              navigate({ to: '/me' })
            }
          } catch (e) {
            const msg = e instanceof ApiError ? e.message : 'Failed to archive workspace'
            toast(msg, { kind: 'error' })
          }
        }}
      />
    </div>
  )
}
